import React, { useState, useCallback } from "react";
import { http } from "../../../../services/HttpService";
import { uiNotification } from "../../../../services/UINotificationService";
import { numberFormat, getCurrencyLabel } from "../../../../Utils/services";
import Loader from "../../../Common/Loader";
import styles from "./scss/posDashboard.module.scss";
import { Button } from "../../../../shared/Button/Button";

const INSTANT_PAYOUT_ERRORS = {
  instant_payout_not_eligible:
    "This bank account is not eligible for instant payouts",
  low_available_balance: "No instant funds available",
  amount_should_be_less_than_or_equal_to_available_balance:
    "Amount exceeds instant available balance",
  payout_bank_not_found: "Bank account not found",
};

const InstantPayout = ({
  availableBalance,
  grossBalance,
  availableBalancePerClinic,
  connectionType,
  onPayoutSuccess,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [bankList, setBankList] = useState([]);
  const [destination, setDestination] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [showClinicTooltip, setShowClinicTooltip] = useState(false);
  const [destinationError, setDestinationError] = useState(false);
  const [amountError, setAmountError] = useState(false);

  const fee = grossBalance - availableBalance;

  const clinicBalances =
    connectionType === "clinic" &&
    availableBalancePerClinic &&
    typeof availableBalancePerClinic === "object"
      ? Object.entries(availableBalancePerClinic).map(([clinic, balance]) => ({
          clinic,
          balance,
        }))
      : [];

  const openModal = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await http.get("pos-add-payout");
      setBankList(res.data.data || []);
      setDestination("");
      setAmount(availableBalance);
      setDescription("");
      setDestinationError(false);
      setAmountError(false);
      setIsOpen(true);
    } catch {
      uiNotification.error("Failed to load bank accounts. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [availableBalance]);

  const closeModal = useCallback(() => setIsOpen(false), []);

  const handleSubmit = useCallback(async () => {
    let hasError = false;
    if (!destination) {
      setDestinationError(true);
      hasError = true;
    } else {
      setDestinationError(false);
    }
    if (!amount || parseFloat(amount) <= 0) {
      setAmountError(true);
      hasError = true;
    } else if (parseFloat(amount) > parseFloat(availableBalance)) {
      uiNotification.error("Amount exceeds instant available balance");
      setAmountError(true);
      hasError = true;
    } else {
      setAmountError(false);
    }
    if (hasError) return;

    setIsLoading(true);
    try {
      await http.post("pos-instant-payout", {
        amount: parseFloat(amount),
        destination,
        ...(description && { description }),
      });
      setIsOpen(false);
      onPayoutSuccess?.();
    } catch (e) {
      const msg = e?.response?.data?.message;
      uiNotification.error(
        INSTANT_PAYOUT_ERRORS[msg] ||
          (msg === "third_party_error" ? e?.response?.data?.data : null) ||
          "Something went wrong, please try again",
      );
    } finally {
      setIsLoading(false);
    }
  }, [destination, amount, availableBalance, description, onPayoutSuccess]);

  return (
    <>
      <div className={styles.instantRow}>
        <span>
          Instant Available
          {clinicBalances.length > 0 && (
            <span
              className={styles.clinicTooltipAnchor}
              onMouseEnter={() => setShowClinicTooltip(true)}
              onMouseLeave={() => setShowClinicTooltip(false)}
            >
              <i className="fa fa-info-circle" aria-hidden="true" />
              {showClinicTooltip && (
                <div className={styles.clinicTooltip}>
                  <strong>Clinic Instant Balances:</strong>
                  <ul>
                    {clinicBalances.map((item, idx) => (
                      <li key={idx}>
                        {item.clinic}:{" "}
                        {numberFormat(item.balance, "currency", 2)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </span>
          )}
        </span>
        <span>{numberFormat(availableBalance, "currency", 2)}</span>
        <Button
          size="tiny"
          onClick={openModal}
          isDisabled={isLoading || availableBalance <= 0}
          className="p-x-10"
        >
          {isLoading ? "Loading..." : "Instant Payout"}
        </Button>
        {fee > 0 && (
          <span className={styles.platformFee}>
            Platform fee: {numberFormat(fee, "currency", 2)}
          </span>
        )}
      </div>

      <div className={isOpen ? "modalOverlay" : "no-display"}>
        <div className="small-popup-outer">
          <div className="small-popup-header">
            <div className="popup-name">Instant Payout</div>
            <a className="small-cross" onClick={closeModal}>
              ×
            </a>
          </div>
          <div className="small-popup-content">
            <div className="juvly-container">
              <div className="row">
                <div className="col-sm-6">
                  <div className="newInputFileldOuter">
                    <div className="newInputLabel">
                      Instant Available Balance
                    </div>
                    <div className="setting-input-outer">
                      <input
                        className="newInputField"
                        type="text"
                        readOnly
                        value={
                          numberFormat(availableBalance, "currency", 2) +
                          " " +
                          getCurrencyLabel().toUpperCase()
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="newInputFileldOuter">
                    <div className="newInputLabel">
                      Amount<span className="setting-require">*</span>
                    </div>
                    <div className="setting-input-outer">
                      <input
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className={
                          amountError
                            ? "newInputField field_error"
                            : "newInputField"
                        }
                        placeholder="Amount to pay out"
                        type="number"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="newInputFileldOuter">
                    <div className="newInputLabel">
                      Send to<span className="setting-require">*</span>
                    </div>
                    <div className="setting-input-outer">
                      <select
                        value={destination}
                        onChange={(e) => setDestination(e.target.value)}
                        className={
                          destinationError
                            ? "newSelectField field_error"
                            : "newSelectField"
                        }
                      >
                        <option value="">Select bank account</option>
                        {bankList.map((obj, idx) => (
                          <option key={idx} value={obj.bank_identifier}>
                            {obj.bank_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="newInputFileldOuter">
                    <div className="newInputLabel">Description</div>
                    <div className="setting-input-outer">
                      <input
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="newInputField"
                        placeholder="Optional"
                        type="text"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
                {fee > 0 && (
                  <div className="col-sm-12">
                    <p className={styles.balanceDisclaimer}>
                      Platform fee: {numberFormat(fee, "currency", 2)} — already
                      deducted from the instant available balance.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="footer-static">
              <a className="new-blue-btn pull-right" onClick={handleSubmit}>
                Pay Out
              </a>
              <a className="new-white-btn pull-right" onClick={closeModal}>
                Cancel
              </a>
            </div>
            <Loader showLoader={isLoading} />
          </div>
        </div>
      </div>
    </>
  );
};

export default InstantPayout;
